import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { Link, NavigateFunction } from "react-router-dom";
import {
  CartSingleton,
  ICartItem,
  SessionExpiredError,
} from "../SharedComponents/CartSingleton";
import { Layout } from "../SharedComponents/Layout";
import { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import { IInstrument } from "../clients/answeringClient";
import CheckoutReviewRow, {
  CheckoutReviewRowDelegate,
} from "./CheckoutReviewRow";
import {
  CheckoutReviewController,
  CheckoutReviewControllerDelegate,
} from "./controller";
import { getUserRolesFromToken } from "../utils/getUserRolesFromToken";
import { trackGtagEvent } from "../utils/analytics";

type CheckoutCartItem = ICartItem & {
  description?: string;
};

interface CheckoutDataState {
  purchaseData: CheckoutCartItem[];
  subtotalCents: number;
  progressiveSubtotalCents: number;
  progressiveDiscountCents: number;
  couponDiscountCents: number;
  totalDiscountCents: number;
  totalCents: number;
  itemsCount: number;
  updatedAt?: string;
}

interface State {
  errorMessage?: string;
  data: CheckoutDataState;
  isLoading: boolean;
  isUpdating: boolean;
}

interface Props {
  navigationHooks: NavigateFunction;
}

export class CheckoutReview
  extends React.Component<Props, State>
  implements CheckoutReviewControllerDelegate, CheckoutReviewRowDelegate {
  private controller: CheckoutReviewController;
  private cartListener = () => this.handleCartUpdated();
  private lastRequestedInstrumentIds: number[] = [];
  private isUnmounted = false;

  constructor(props: Props) {
    super(props);
    this.controller = new CheckoutReviewController(this);
    this.state = {
      data: {
        purchaseData: [],
        subtotalCents: 0,
        progressiveSubtotalCents: 0,
        progressiveDiscountCents: 0,
        couponDiscountCents: 0,
        totalDiscountCents: 0,
        totalCents: 0,
        itemsCount: 0,
        updatedAt: undefined,
      },
      isLoading: true,
      isUpdating: false,
    };
  }

  componentDidMount(): void {
    this.isUnmounted = false;
    CartSingleton.shared.addCartUpdatedListener(this.cartListener);
    this.loadCart();
  }

  componentWillUnmount(): void {
    this.isUnmounted = true;
    CartSingleton.shared.removeCartUpdatedListener(this.cartListener);
  }

  didGetInstruments(instruments: IInstrument[]): void {
    const purchaseData = this.state.data.purchaseData.map((item) => {
      const instrument = instruments.find(
        (instrumentItem) => instrumentItem.id === item.instrumentId
      );

      if (!instrument) {
        return item;
      }

      return {
        ...item,
        description: instrument.description,
      };
    });

    this.setState({
      data: {
        ...this.state.data,
        purchaseData,
      },
    });
  }

  private async loadCart() {
    this.setState({
      isLoading: true,
      isUpdating: false,
      errorMessage: undefined,
    });

    try {
      await CartSingleton.shared.refreshCart();
    } catch (error) {
      // No retry can revive a dead session, so "Tente novamente" would strand
      // the user on an empty screen.
      if (error instanceof SessionExpiredError) {
        this.applicationShouldLogout();
        return;
      }
      this.setState({
        isLoading: false,
        isUpdating: false,
        errorMessage:
          "Não foi possível carregar o carrinho. Tente novamente.",
      });
    }
  }

  private handleCartUpdated() {
    const snapshot = CartSingleton.shared.getCartSnapshot();

    if (!snapshot) {
      this.lastRequestedInstrumentIds = [];
      this.setState({
        isLoading: false,
        isUpdating: false,
        data: {
          purchaseData: [],
          subtotalCents: 0,
          progressiveSubtotalCents: 0,
          progressiveDiscountCents: 0,
          couponDiscountCents: 0,
        totalDiscountCents: 0,
        totalCents: 0,
        itemsCount: 0,
        updatedAt: undefined,
      },
    });
      return;
    }

    if (snapshot.items.length === 0) {
      this.lastRequestedInstrumentIds = [];
    }

    const previousByInstrument = new Map(
      this.state.data.purchaseData.map((item) => [item.instrumentId, item])
    );

    const purchaseData: CheckoutCartItem[] = snapshot.items.map((item) => {
      const previous = previousByInstrument.get(item.instrumentId);
      return {
        ...item,
        description: previous?.description,
      };
    });

    this.setState({
      isLoading: false,
      isUpdating: false,
      errorMessage: undefined,
      data: {
        purchaseData,
        subtotalCents: snapshot.subtotalCents,
        progressiveSubtotalCents: snapshot.progressiveSubtotalCents,
        progressiveDiscountCents: snapshot.progressiveDiscountCents,
        couponDiscountCents: snapshot.couponDiscountCents,
        totalDiscountCents: snapshot.totalDiscountCents,
        totalCents: snapshot.totalCents,
        itemsCount: snapshot.itemsCount,
        updatedAt: snapshot.updatedAt,
      },
    });

    const instrumentIds = snapshot.items
      .map((item) => item.instrumentId)
      .sort((a, b) => a - b);

    if (
      instrumentIds.length > 0 &&
      !this.areInstrumentListsEqual(this.lastRequestedInstrumentIds, instrumentIds)
    ) {
      this.lastRequestedInstrumentIds = [...instrumentIds];
      const token = localStorage.getItem("AccessToken");
      if (!token) {
        this.applicationShouldLogout();
        return;
      }
      this.controller.getManagedInstruments(token, instrumentIds);
    }
  }

  private areInstrumentListsEqual(a: number[], b: number[]) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((value, index) => value === b[index]);
  }

  applicationShouldLogout(): void {
    logout();
  }

  genericError(message?: string): void {
    this.setState({
      isLoading: false,
      isUpdating: false,
      errorMessage:
        message ?? "Não foi possível carregar os itens do carrinho.",
    });
  }

  async didChangeQuantity(instrumentId: number, quantity: number): Promise<void> {
    if (this.state.isUpdating) {
      return;
    }

    const currentItem = this.state.data.purchaseData.find(
      (item) => item.instrumentId === instrumentId
    );
    if (currentItem && currentItem.quantity === quantity) {
      return;
    }

    this.setState({
      isUpdating: true,
      errorMessage: undefined,
    });

    try {
      await CartSingleton.shared.setCartInstrument(instrumentId, quantity);
      if (!CartSingleton.shared.getCartSnapshot()) {
        // Re-read only on the resolve path: setCartInstrument can invalidate
        // the snapshot without dispatching (see reloadAfterWrite). From the
        // catch it would wipe the failure message, and loadCart's isLoading
        // flip blanks the screen mid-edit.
        await CartSingleton.shared.refreshCart();
      }
    } catch (error) {
      console.warn("Cart update failed", error);
      if (this.isUnmounted) {
        return;
      }
      if (error instanceof SessionExpiredError) {
        this.applicationShouldLogout();
        return;
      }
      this.setState({
        errorMessage:
          "Não foi possível atualizar o item. Atualize a página ou tente novamente.",
      });
    } finally {
      if (!this.isUnmounted) {
        this.setState({ isUpdating: false });
      }
    }
  }

  // Internals
  didClickGoToPayment() {
    this.trackInitiateCheckout();
    this.props.navigationHooks("/checkout/payment");
  }

  didClickGoToBuy() {
    this.props.navigationHooks("/instruments");
  }

  renderLoading() {
    return (
      <Box flex={1} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  renderPurchaseRow() {
    return this.state.data.purchaseData.map((item, index) => (
      <CheckoutReviewRow
        item={item}
        key={`cart_item_${item.id}_${item.instrumentId}`}
        delegate={this}
        disabled={this.state.isUpdating}
        index={index}
      />
    ));
  }

  renderPuchaseDataTable() {
    const {
      purchaseData,
      subtotalCents,
      couponDiscountCents,
      progressiveDiscountCents,
      totalCents,
      itemsCount,
      updatedAt,
    } =
      this.state.data;
    const { errorMessage } = this.state;

    if (purchaseData.length === 0) {
      return (
        <Box
          flex={1}
          overflow="auto"
          display="flex"
          justifyContent="center"
        >
          <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                p: { xs: 4, md: 6 },
                textAlign: "center",
                border: "1px solid rgba(0, 0, 0, 0.1)"
              }}
            >
              <Stack spacing={3} alignItems="center">
                {errorMessage && (
                  <Alert severity="error" sx={{ width: "100%" }}>
                    {errorMessage}
                  </Alert>
                )}
                <Typography variant="h4" fontWeight={600}>
                  Seu carrinho está vazio
                </Typography>
                <Typography color="text.secondary" maxWidth={420}>
                  Procure pelos instrumentos disponíveis e adicione os itens que
                  deseja adquirir para continuar o processo de compra.
                </Typography>
                <Link to="/instruments" style={{ textDecoration: "none" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: 999,
                      px: 4,
                      py: 1.25,
                    }}
                  >
                    Explorar instrumentos
                  </Button>
                </Link>
              </Stack>
            </Paper>
          </Container>
        </Box>
      );
    }

    const formatCurrency = new Intl.NumberFormat("pt-br", {
      currency: "BRL",
      style: "currency",
    });
    const totalPriceFormatted = formatCurrency.format(totalCents / 100);
    const subtotalFormatted = formatCurrency.format(subtotalCents / 100);
    const progressiveDiscountFormatted =
      progressiveDiscountCents > 0
        ? formatCurrency.format(progressiveDiscountCents / 100)
        : null;
    const couponDiscountFormatted =
      couponDiscountCents > 0
        ? formatCurrency.format(couponDiscountCents / 100)
        : null;

    const priceChangedItems = purchaseData.filter((item) => item.priceChanged);
    const updatedAtLabel = updatedAt
      ? new Date(updatedAt).toLocaleString("pt-BR")
      : null;

    return (
      <Box
        flex={1}
        overflow="auto"
        display="flex"
        flexDirection="column"
        alignItems="stretch"
      >
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
            >

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Typography variant="h4" fontWeight={600}>
                  Resumo do Carrinho
                </Typography>
                <Chip
                  label={`${itemsCount} ${itemsCount === 1 ? "item" : "itens"
                    }`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />

              </Stack>
              {updatedAtLabel && (
                <Typography variant="body2" color="text.secondary">
                  Atualizado em {updatedAtLabel}
                </Typography>
              )}
            </Stack>

            {priceChangedItems.length > 0 && (
              <Alert severity="warning">
                {priceChangedItems.length === 1 ? (
                  <>
                    O preço do instrumento{" "}
                    <strong>{priceChangedItems[0].title}</strong> foi atualizado.
                    Confira o novo valor antes de finalizar a compra.
                  </>
                ) : (
                  "Alguns itens tiveram o preço atualizado. Confira os novos valores antes de finalizar a compra."
                )}
              </Alert>
            )}

            {errorMessage && (
              <Alert severity="error">{errorMessage}</Alert>
            )}

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                p: { xs: 1, md: 2 },
                border: "1px solid rgba(0, 0, 0, 0.1)"
              }}
            >
              <Stack spacing={{ xs: 2 }}>
                {this.renderPurchaseRow()}
              </Stack>
            </Paper>

            <Stack spacing={1}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {subtotalFormatted}
                </Typography>
              </Stack>

              {couponDiscountFormatted && (
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <Typography variant="body1" fontWeight={500} color="text.secondary">
                    Desconto do cupom
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }} color="#F19321">
                    - {couponDiscountFormatted}
                  </Typography>
                </Stack>
              )}

              {progressiveDiscountFormatted && (
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    Descontos progressivos aplicados
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }} color="#F19321">
                    - {progressiveDiscountFormatted}
                  </Typography>
                </Stack>
              )}
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
                Total do carrinho
              </Typography>
              <Typography
                variant="h4"
                color="primary"
                sx={{ fontWeight: 700 }}
              >
                {totalPriceFormatted}
              </Typography>
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="flex-end"
              alignItems={{ xs: "stretch", md: "center" }}
            >

              <Button
                variant="outlined"
                color="secondary"
                onClick={() => this.didClickGoToBuy()}
              >
                Adicionar mais itens
              </Button>


              <Button
                variant="contained"
                color="primary"
                onClick={() => this.didClickGoToPayment()}
                disabled={this.state.isUpdating}
              >
                Ir para a finalização da compra
              </Button>


              {this.state.isUpdating && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ mt: { xs: 1, md: 0 } }}
                >
                  <CircularProgress size={22} color="primary" />
                </Box>
              )}
            </Stack>
          </Stack>
        </Container>
      </Box>
    );
  }

  render() {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
        }}
      >
        <Layout>
          {/* <Header logoutEnabled={true}/> */}
          {/* <Header2 /> */}
          {this.state.isLoading
            ? this.renderLoading()
            : this.renderPuchaseDataTable()}
        </Layout>
      </div>
    );
  }

  private trackInitiateCheckout() {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.cookie.includes("catvanteAnalyticsCookiesAccepted=true")
    ) {
      return;
    }

    const { purchaseData, totalCents, itemsCount } = this.state.data;

    if (purchaseData.length === 0 || itemsCount <= 0) {
      return;
    }

    const contents = purchaseData.map((item) => {
      const unitCents =
        item.discountedUnitPriceCents > 0
          ? item.discountedUnitPriceCents
          : item.unitPriceCents;

      return {
        id: String(item.instrumentId),
        quantity: item.quantity,
        item_price: unitCents / 100,
      };
    });

    const contentIds = contents.map((item) => item.id);
    const roles = getUserRolesFromToken();

    const fbq = window.fbq;
    if (fbq) {
      fbq("track", "InitiateCheckout", {
        content_ids: contentIds,
        contents,
        content_type: "product",
        num_items: itemsCount,
        value: totalCents / 100,
        currency: "BRL",
        roles,
      });
    }

    trackGtagEvent("begin_checkout", {
      currency: "BRL",
      value: totalCents / 100,
      items: contents.map((item) => ({
        item_id: item.id,
        quantity: item.quantity,
        price: item.item_price,
      })),
      items_count: itemsCount,
      roles,
    });
  }
}

export default withNavigationHooks(CheckoutReview);
